// apps/web/src/features/clients/components/LegalEntityFormModal.tsx
//
// Add OR edit a `ClientLegalEntity`. The two modes are genuinely different
// forms, so they are separate components behind one entry point:
//
//   - Add    — type + name + INN + the entity's FIRST requisites set.
//   - Edit   — identity only (name + INN). `type` is immutable post-create and
//              the bank sets are edited one-by-one through
//              `RequisitesSetFormModal`.
//
// INVARIANT (#2242 FR-2): an identity-only PATCH must not validate or resend
// requisites text the operator cannot see in this dialog — that would reject a
// name fix because of bank text edited elsewhere, or overwrite it.
//
// Child-modal contract (Decision 21): this modal guards its OWN dirty form and,
// on a successful submit, closes directly without dirtying the parent.
import { useId, useMemo, type CSSProperties } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  CLIENT_LEGAL_ENTITY_REQUISITES_MAX_LENGTH,
  CLIENT_LEGAL_ENTITY_NAME_MAX_LENGTH,
  ClientLegalEntityInputSchema,
  ClientLegalEntityType,
  PatchClientLegalEntityInputSchema,
  isValidInnIp,
  isValidInnOoo,
  type ClientLegalEntity,
  type ClientLegalEntityInput,
  type PatchClientLegalEntityInput,
} from '@magimp/shared';
import { useAddClientLegalEntity, usePatchClientLegalEntity } from '@magimp/shared-hooks';
import {
  Button,
  EditorModal,
  FormRow,
  Select,
  Textarea,
  type SelectOption,
} from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { useClientFieldError } from '../useClientFieldError.js';
import { useClientSubmitError } from '../useClientSubmitError.js';
import { useMobileFieldStyle, useTouchActionStyle } from '../useMobileFieldStyle.js';
import { EditorCancelButton } from './EditorCancelButton.js';
import { FieldLengthHint } from './FieldLengthHint.js';
import { LegalInnField, LegalNameField, LegalSubmitErrorBanner } from './LegalEntityFormFields.js';

export interface LegalEntityFormModalProps {
  clientId: string;
  /** Owning client, named in the dialog title so the target is never guessed. */
  clientName: string;
  entity?: ClientLegalEntity;
  onClose: () => void;
}

export function LegalEntityFormModal(props: LegalEntityFormModalProps) {
  const { entity } = props;
  return entity ? (
    <LegalEntityEditForm {...props} entity={entity} />
  ) : (
    <LegalEntityAddForm {...props} />
  );
}

type AddForm = {
  type: ClientLegalEntityType;
  name: string;
  inn: string;
  requisites: string;
};

function LegalEntityAddForm({ clientId, clientName, onClose }: LegalEntityFormModalProps) {
  const { t } = useTranslation(['clients', 'common']);
  const showToast = useToastStore((s) => s.show);
  const add = useAddClientLegalEntity(clientId);
  const submitError = useClientSubmitError();
  const fieldError = useClientFieldError();
  const fieldStyle = useMobileFieldStyle();
  const actionStyle = useTouchActionStyle();
  const counterId = useId();
  const formId = useId();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<AddForm>({
    resolver: zodResolver(ClientLegalEntityInputSchema as never),
    defaultValues: {
      type: ClientLegalEntityType.OOO,
      name: '',
      inn: '',
      requisites: '',
    },
  });

  const type = watch('type');
  const requisitesLength = (watch('requisites') ?? '').length;
  const typeOptions: SelectOption[] = [
    { value: ClientLegalEntityType.OOO, label: t('clients:legalType.OOO') },
    { value: ClientLegalEntityType.IP, label: t('clients:legalType.IP') },
  ];

  const onSubmit = (values: AddForm) => {
    submitError.clear();
    const payload: ClientLegalEntityInput = values;
    add.mutate(payload, {
      onSuccess: () => {
        // Reset so RHF marks the form clean, then close directly — the parent
        // must not observe a dirty child.
        reset(values);
        showToast({ tone: 'success', text: t('clients:toasts.legalAdded') });
        onClose();
      },
      onError: (err) =>
        submitError.report(err, (field, message) => {
          if (field === 'inn') setError('inn', { message }, { shouldFocus: true });
          if (field === 'name') setError('name', { message }, { shouldFocus: true });
        }),
    });
  };

  return (
    <EditorModal
      open={true}
      title={t('clients:modals.addLegalTitle', { client: clientName })}
      width={520}
      narrowFullWidth
      dirty={isDirty}
      busy={add.isPending}
      onConfirmClose={onClose}
      footer={
        <FormFooter
          onClose={onClose}
          formId={formId}
          pending={add.isPending}
          submitLabel={t('common:actions.create')}
          actionStyle={actionStyle}
        />
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        <LegalSubmitErrorBanner state={submitError.state} />

        <FormRow
          label={t('clients:fields.legalType')}
          hint={t('clients:hints.legalTypeImmutable')}
          error={fieldError(errors.type, { kind: 'text' })}
        >
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(v) => field.onChange(v as ClientLegalEntityType)}
                options={typeOptions}
                // The one native control in this form: it takes the same 16 px
                // (no focus auto-zoom) and 44 px (tappable) presentation as the
                // text fields and actions around it. Desktop is unchanged.
                style={{ ...fieldStyle, ...actionStyle }}
              />
            )}
          />
        </FormRow>
        <LegalNameField
          control={control}
          name="name"
          error={fieldError(errors.name, {
            kind: 'text',
            max: CLIENT_LEGAL_ENTITY_NAME_MAX_LENGTH,
          })}
          fieldStyle={fieldStyle}
        />
        <LegalInnField
          control={control}
          name="inn"
          error={fieldError(errors.inn, { kind: 'inn', legalType: type })}
          type={type}
          fieldStyle={fieldStyle}
        />
        <FormRow
          label={t('clients:fields.requisites')}
          hint={t('clients:hints.firstRequisites')}
          error={fieldError(errors.requisites, {
            kind: 'text',
            max: CLIENT_LEGAL_ENTITY_REQUISITES_MAX_LENGTH,
          })}
        >
          <Controller
            control={control}
            name="requisites"
            render={({ field }) => (
              <Textarea
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                rows={7}
                invalid={!!errors.requisites}
                ariaDescribedBy={counterId}
                style={fieldStyle}
              />
            )}
          />
        </FormRow>
        <FieldLengthHint
          id={counterId}
          length={requisitesLength}
          max={CLIENT_LEGAL_ENTITY_REQUISITES_MAX_LENGTH}
        />
      </form>
    </EditorModal>
  );
}

type EditForm = { name: string; inn: string };

function LegalEntityEditForm({
  clientId,
  clientName,
  entity,
  onClose,
}: LegalEntityFormModalProps & { entity: ClientLegalEntity }) {
  const { t } = useTranslation(['clients', 'common']);
  const showToast = useToastStore((s) => s.show);
  const patch = usePatchClientLegalEntity(clientId, entity.id);
  const submitError = useClientSubmitError();
  const fieldError = useClientFieldError();
  const fieldStyle = useMobileFieldStyle();
  const actionStyle = useTouchActionStyle();
  const formId = useId();

  // WHY: the wire PATCH omits the immutable `type`, so the shared schema can
  // only require an INN valid for SOME legal form — a 12-digit IP INN typed
  // into an OOO row passes it and comes back as a server rejection. This
  // dialog knows the entity's type, so it applies the SAME type-bound rule the
  // create path has, locally, on the field.
  const schema = useMemo(
    () =>
      PatchClientLegalEntityInputSchema.superRefine((values: PatchClientLegalEntityInput, ctx) => {
        if (typeof values.inn !== 'string') return;
        const valid =
          entity.type === ClientLegalEntityType.OOO
            ? isValidInnOoo(values.inn)
            : isValidInnIp(values.inn);
        if (!valid) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['inn'], message: 'invalid INN' });
        }
      }),
    [entity.type],
  );

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<EditForm>({
    // Patch schema, not the create schema: `requisites` is NOT part of this
    // dialog, so requiring it here would block an identity-only fix.
    resolver: zodResolver(schema as never),
    defaultValues: { name: entity.name, inn: entity.inn },
  });

  const onSubmit = (values: EditForm) => {
    submitError.clear();
    const payload: PatchClientLegalEntityInput = {};
    if (dirtyFields.name) payload.name = values.name;
    if (dirtyFields.inn) payload.inn = values.inn;
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    patch.mutate(payload, {
      onSuccess: () => {
        reset(values);
        showToast({ tone: 'success', text: t('clients:toasts.legalSaved') });
        onClose();
      },
      onError: (err) =>
        submitError.report(err, (field, message) => {
          if (field === 'inn') setError('inn', { message }, { shouldFocus: true });
          if (field === 'name') setError('name', { message }, { shouldFocus: true });
        }),
    });
  };

  return (
    <EditorModal
      open={true}
      title={t('clients:modals.editLegalTitle', { client: clientName })}
      width={520}
      narrowFullWidth
      dirty={isDirty}
      busy={patch.isPending}
      onConfirmClose={onClose}
      footer={
        <FormFooter
          onClose={onClose}
          formId={formId}
          pending={patch.isPending}
          submitLabel={t('common:actions.save')}
          actionStyle={actionStyle}
        />
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        <LegalSubmitErrorBanner state={submitError.state} />

        <FormRow label={t('clients:fields.legalType')} hint={t('clients:hints.legalTypeImmutable')}>
          <div style={{ padding: 'var(--s-2) 0', color: 'var(--fg-2)' }}>
            {t(`clients:legalType.${entity.type}`)}
          </div>
        </FormRow>
        <LegalNameField
          control={control}
          name="name"
          error={fieldError(errors.name, {
            kind: 'text',
            max: CLIENT_LEGAL_ENTITY_NAME_MAX_LENGTH,
          })}
          fieldStyle={fieldStyle}
        />
        <LegalInnField
          control={control}
          name="inn"
          error={fieldError(errors.inn, { kind: 'inn', legalType: entity.type })}
          type={entity.type}
          fieldStyle={fieldStyle}
        />
      </form>
    </EditorModal>
  );
}

function FormFooter({
  onClose,
  formId,
  pending,
  submitLabel,
  actionStyle,
}: {
  onClose: () => void;
  formId: string;
  pending: boolean;
  submitLabel: string;
  actionStyle: CSSProperties;
}) {
  const { t } = useTranslation(['common']);
  return (
    <>
      <EditorCancelButton
        onClose={onClose}
        label={t('common:actions.cancel')}
        style={actionStyle}
      />
      <Button
        type="submit"
        form={formId}
        kind="primary"
        style={actionStyle}
        disabled={pending}
        data-testid="client-legal-submit"
      >
        {submitLabel}
      </Button>
    </>
  );
}
